import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchWalletSummary,
  fetchWalletTransactions,
  type WalletSummaryResponse,
  type WalletTransactionsResponse,
} from '@/services/walletService';

const logoutSuccess = createAction('auth/logoutSuccess');

export const loadWalletSummary = createAsyncThunk('wallet/loadSummary', async () => {
  const summary = await fetchWalletSummary();
  return summary;
});

export const loadWalletTransactions = createAsyncThunk(
  'wallet/loadTransactions',
  async (params: { page?: number; pageSize?: number } | undefined) => {
    const transactions = await fetchWalletTransactions(params ?? {});
    return transactions;
  },
);

interface WalletSummaryState {
  data: WalletSummaryResponse | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface WalletTransactionsState {
  data: WalletTransactionsResponse['data'];
  pagination: WalletTransactionsResponse['pagination'];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface WalletState {
  summary: WalletSummaryState;
  transactions: WalletTransactionsState;
}

const initialState: WalletState = {
  summary: {
    data: null,
    status: 'idle',
    error: null,
  },
  transactions: {
    data: [],
    pagination: {
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0,
    },
    status: 'idle',
    error: null,
  },
};

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    resetWalletState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(loadWalletSummary.pending, state => {
        state.summary.status = 'loading';
        state.summary.error = null;
      })
      .addCase(loadWalletSummary.fulfilled, (state, action) => {
        state.summary.status = 'succeeded';
        state.summary.data = action.payload;
      })
      .addCase(loadWalletSummary.rejected, (state, action) => {
        state.summary.status = 'failed';
        state.summary.error = action.error.message ?? 'Failed to load wallet summary';
      })
      .addCase(loadWalletTransactions.pending, state => {
        state.transactions.status = 'loading';
        state.transactions.error = null;
      })
      .addCase(loadWalletTransactions.fulfilled, (state, action) => {
        state.transactions.status = 'succeeded';
        state.transactions.data = action.payload.data;
        state.transactions.pagination = action.payload.pagination;
      })
      .addCase(loadWalletTransactions.rejected, (state, action) => {
        state.transactions.status = 'failed';
        state.transactions.error = action.error.message ?? 'Failed to load wallet transactions';
      })
      .addCase(logoutSuccess, () => initialState);
  },
});

export const { resetWalletState } = walletSlice.actions;

type WalletSliceAwareState = { wallet: WalletState };

export const selectWalletSummary = (state: WalletSliceAwareState) => state.wallet.summary;
export const selectWalletTransactions = (state: WalletSliceAwareState) => state.wallet.transactions;
export const selectWalletBalance = (state: WalletSliceAwareState) => state.wallet.summary.data?.balance ?? 0;
export const selectWalletCurrency = (state: WalletSliceAwareState) => state.wallet.summary.data ? 'USD' : null;

export default walletSlice.reducer;
